export type Rng = () => number;

export function createRng(seed: number): Rng {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let value = state;
    value = Math.imul(value ^ (value >>> 15), value | 1);
    value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
    return ((value ^ (value >>> 14)) >>> 0) / 0x1_0000_0000;
  };
}

export function deriveSeed(seed: number, salt: number): number {
  let value = (seed + Math.imul(salt + 1, 0x9e3779b9)) >>> 0;
  value = Math.imul(value ^ (value >>> 16), 0x21f0aaad);
  value = Math.imul(value ^ (value >>> 15), 0x735a2d97);
  return (value ^ (value >>> 15)) >>> 0;
}

export function fnv1a32Hex(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

export function nextInt(rng: Rng, minInclusive: number, maxInclusive: number): number {
  if (!Number.isInteger(minInclusive) || !Number.isInteger(maxInclusive)) {
    throw new Error('nextInt bounds must be integers.');
  }
  if (maxInclusive < minInclusive) {
    throw new Error('nextInt maximum must be greater than or equal to its minimum.');
  }
  return minInclusive + Math.floor(rng() * (maxInclusive - minInclusive + 1));
}

export function shuffle<T>(rng: Rng, values: readonly T[]): T[] {
  const result = [...values];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = nextInt(rng, 0, index);
    const value = result[index] as T;
    result[index] = result[swapIndex] as T;
    result[swapIndex] = value;
  }
  return result;
}

export function pickOne<T>(rng: Rng, values: readonly T[]): T {
  if (values.length === 0) {
    throw new Error('Cannot pick from an empty array.');
  }
  return values[nextInt(rng, 0, values.length - 1)] as T;
}

export function popRandom<T>(rng: Rng, values: T[]): T | undefined {
  if (values.length === 0) {
    return undefined;
  }
  const index = nextInt(rng, 0, values.length - 1);
  const selected = values[index];
  const last = values.pop();
  if (index < values.length && last !== undefined) {
    values[index] = last;
  }
  return selected;
}
